import React, { useState, useEffect, useRef, useMemo } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment-timezone';
import './style.scss';
import Icon from 'components/Utils/NewIcon';
import DefaultModal from 'components/Utils/DefaultModal';
import PreviewDocumentModal from 'components/Modals/PreviewDocumentModal';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import AddDocumentAttachmentModal from 'components/Modals/AddDocumentAttachmentModal';
import MiscUtils from 'services/MiscUtils';
import { DefaultSwitch } from 'services/UIUtils';
import { TopBar } from 'components/Patient/CPP/CPPInput/Inputs/CPPInputCommonViews';
import DocumentTemplateList from 'components/HealthRecords/Documents/DocumentTemplateList';
import DocumentUtils from 'services/DocumentUtils';
import CPPUtils from 'services/CPPUtils';
import ContactsSearchModal from 'components/Modals/ContactsSearchModal';
import ContactsSearch from 'components/Contacts/ContactsSearch';
import ContactBadge from 'components/Contacts/ContactBadge';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { IconButton } from '@material-ui/core';
import { GetStaff, InternOf, IsAdmin } from 'careand-redux/selectors';
import ProgressMessage from 'components/Utils/ProgressMessage';
import { uiActions } from 'careand-redux/slices/ui/ui';
import PerformanceUtils from 'services/PerformanceUtils';
import { NewReferralRequest } from 'scenes/HomeStaff/ReferralsDirectory/ReferralRequest';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import StackItemsView from 'components/Common/StackItemsView';
import { useReduxForm } from 'hooks/useReduxForm';
import { Physician } from '../../../../generated/api';

const DEFAULT_OBJECT = {};

const referralSchema = z.object({
  text: z.string().optional().or(z.literal('')),
  cpp: z.string().optional().or(z.literal('')),
  attachments: z.any().optional(),
  send_to: z.any().optional(),
  id: z.any().optional(),
  referral_type: z.string().optional().or(z.literal('')),
  hide: z.boolean().optional(),
  urgent: z.boolean().optional(),
  include_cover: z.boolean().optional(),
  sendFax: z.boolean().optional(),
  sendEmail: z.boolean().optional(),
});

type ReferralValues = z.infer<typeof referralSchema>;

interface NewReferralProps {
  patient: any;
  form: string;
  onClose?: () => void;
  copyFrom?: any;
  physicianId?: string;
  initialFormState?: Record<string, any>;
  text?: string;
  send_to?: any;
}

const NewReferral = ({
  patient,
  form,
  onClose,
  copyFrom,
  physicianId,
  initialFormState,
}: NewReferralProps) => {
  const patientId = patient.id;

  const dispatch = useAppDispatch();

  const { activeUserId } = useAppSelector((state) => state.authentication);
  const templates = useAppSelector((state) => state.templates.templates);
  const patientHealthRecords = useAppSelector(
    (state) => state.healthRecords.byId[patientId]?.healthRecords,
  );
  const patientDocuments = useAppSelector(
    (state) => state.healthRecords.byId[patientId]?.documents,
  );
  const patientPrescriptions = useAppSelector(
    (state) => state.healthRecords.byId[patientId]?.prescriptions,
  );
  const isIntern = useAppSelector(InternOf);
  const isAdmin = useAppSelector(IsAdmin);

  const referringPhysician = useAppSelector((state) => GetStaff(state, physicianId));
  const physician = useAppSelector(
    (state) =>
      state.physicians.byId[
        (referringPhysician as Physician)?.intern_of! || referringPhysician?.id!
      ],
  );

  const { control, handleSubmit, setValue, watch, reset } = useReduxForm<ReferralValues>(form, {
    resolver: zodResolver(referralSchema),
    destroyOnUnmount: false,
    defaultValues: {
      text: '',
      cpp: '',
      attachments: [],
      send_to: undefined,
      id: undefined,
      referral_type: '',
      hide: false,
      urgent: false,
      include_cover: true,
      sendFax: false,
      sendEmail: false,
    },
  });

  const text = watch('text') || '';
  const cppText = watch('cpp') || '';
  const send_to = watch('send_to');
  const formAttachments: any[] = watch('attachments') || [];
  const hideFromPatient = watch('hide');

  const [loading, setLoading] = useState(false);
  const [attachmentModal, setAttachmentModal] = useState(false);
  const [referralModal, setReferralModal] = useState(false);
  const [previewModal, setPreviewModal] = useState(false);
  const [pdfData, setPdfData] = useState<any>();
  const [firstLoad, setFirstLoad] = useState(true);
  const [confirmationModal, setConfirmationModal] = useState<boolean | undefined>(false);
  const [requestModal, setRequestModal] = useState(false);
  const acknowledgedAvailability = useRef(false);
  const [includeCover, setIncludeCover] = useState(true);
  const [includeCpp, setIncludeCpp] = useState(false);

  const wrappedAttachments = useMemo(
    () => formAttachments.map((filename: string) => ({ id: filename, filename })),
    [formAttachments],
  );

  const isUnavailable = useMemo(() => {
    return moment.unix(send_to?.unavailable_until ?? 0).isAfter(moment());
  }, [send_to]);

  const loadedHr = useMemo(
    () => Object.keys(patientHealthRecords ?? {}).length > 0,
    [patientHealthRecords],
  );

  // Apply incoming values directly to RHF. Routing them through redux
  // (setFormValues) relies on useReduxForm's external-change sync, which is
  // skipped while the throttled local sync is pending — importDefaultLetter's
  // setValue arms that throttle in the same mount pass, so the dispatched
  // values were dropped and then overwritten by the default letter.
  const applyFormValues = (values: Record<string, any>) => {
    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        setValue(key as keyof ReferralValues, value);
      }
    });
  };

  useEffect(() => {
    if (!initialFormState) {
      return;
    }
    applyFormValues(initialFormState);
  }, [initialFormState]);

  useEffect(() => {
    if (!loadedHr) {
      dispatch(api.HealthRecords.getCPP({ patientId }));
    }
  }, [dispatch, patientId, loadedHr]);

  useEffect(() => {
    if (loadedHr) {
      if (firstLoad && !text && !copyFrom?.text) {
        importDefaultLetter();
      }
      setFirstLoad(false);
    }
  }, [firstLoad, text, patient, physician, patientHealthRecords, loadedHr]);

  function importDefaultLetter() {
    if (!physician) {
      return;
    }
    // When the dedicated CPP page is on, keep CPP off the letter body so it isn't
    // duplicated (once in the letter, once on the dedicated CPP page).
    let newText = CPPUtils.getReferralText(
      patient,
      physician,
      includeCpp ? '' : CPPUtils.getReferralCPPInfo(patientHealthRecords),
    );
    setValue('text', newText);
  }

  function toggleCpp(checked: boolean) {
    setIncludeCpp(checked);
    // Only snapshot once health records have actually loaded. getReferralCPPInfo
    // cannot distinguish 'no records loaded' from 'no allergies' — both produce
    // '\nAllergies: NKDA\n', which reads as a verified clinical finding. The
    // toggle itself is also disabled until loadedHr (see the switch below),
    // mirroring the gate already used for the cover-letter textarea.
    if (checked && loadedHr && !cppText) {
      setValue('cpp', CPPUtils.getReferralCPPInfo(patientHealthRecords));
    }
  }

  useEffect(() => {
    if (copyFrom && !text && firstLoad) {
      applyFormValues(copyFrom);
      if (copyFrom.cpp) {
        setIncludeCpp(true);
      }
    }
  }, [copyFrom, text, firstLoad]);

  function prepareReferral(values: ReferralValues) {
    return {
      ...PerformanceUtils.clone(values),
      id: copyFrom?.id,
      clinic_id: copyFrom?.clinic_id,
      text: includeCover ? text : undefined,
      cpp: includeCpp ? cppText : undefined,
      patient_id: patient.id,
      physician_id: physician?.id || activeUserId,
      referral_type: undefined,
      referral_comments: copyFrom?.referral_comments,
    };
  }

  async function onClickPreview() {
    if (isUnavailable && !acknowledgedAvailability.current) {
      return setConfirmationModal(true);
    }
    return handleSubmit(previewReferralWrapper)();
  }

  async function previewReferralWrapper(values: ReferralValues) {
    let referral = prepareReferral(values);
    setLoading(true);
    let result = await dispatch(api.HealthRecords.previewReferral({ referral }), {
      errorTitle: 'Referral',
    });
    setLoading(false);
    if (result) {
      let url = MiscUtils.getBlobUrl(result);
      setPdfData({ url, type: result.type });
      setPreviewModal(true);
    }
  }

  async function addReferralWrapper(values: ReferralValues) {
    let sendFax = values.sendFax === true;
    let sendEmail = values.sendEmail === true;
    let cleanValues = { ...values };
    delete cleanValues.sendFax;
    delete cleanValues.sendEmail;
    let referral = prepareReferral(cleanValues);

    setLoading(true);
    setPreviewModal(false);

    const options = {
      title: 'Referral',
      successText: 'Referral added successfully!',
    };

    let result = isIntern
      ? await dispatch(api.HealthRecords.addReferralAsIntern({ referral }), options)
      : await dispatch(
          api.HealthRecords.addReferral({
            referral,
            fax: sendFax,
            email: sendEmail,
          }),
          options,
        );

    setLoading(false);

    if (result) {
      if (!isIntern) {
        //load updated consultants and faxes
        dispatch(api.HealthRecords.getCPP({ patientId: referral.patient_id }));
      }
      reset();
      if (onClose) onClose();
    }
  }

  async function addReferralWithFax(values: ReferralValues) {
    await addReferralWrapper({ ...values, sendFax: true });
  }

  async function addReferralWithEmail(values: ReferralValues) {
    await addReferralWrapper({ ...values, sendEmail: true });
  }

  function onPinToSide() {
    dispatch(
      uiActions.addPinnedDocument({
        id: `referral-${patient.id}`,
        type: 'referral',
        patientId: patient.id,
      }),
    );
    setRequestModal(false);
    if (onClose) onClose();
  }

  const hasContent =
    (includeCover && !!text) || (includeCpp && !!cppText) || formAttachments.length > 0;
  const previewDisabled = !send_to || !hasContent;

  async function onSaveDocument(document: any) {
    setReferralModal(false);
    let attachments = [document.filename, ...(formAttachments || [])];
    setValue('attachments', attachments);
    if (document.template_id) {
      let template = templates.find(({ id }: any) => id === document.template_id);
      if (template?.fax) {
        addContactFromFax(template.fax);
      }
    }
  }

  async function addContactFromFax(fax: string) {
    let name = fax.split(' ').join('').split('(').join('').split(')').join('');
    let contacts = await dispatch(api.Contacts.getPhysicianContacts({ name, limit: 1 }));
    if (contacts?.length! > 0) {
      onSelectContact(contacts?.[0]);
    }
  }

  function onSelectContact(contact: any) {
    setValue('send_to', contact);
  }

  return (
    <div className="new-referral">
      {onClose !== undefined && (
        <TopBar title="Referrals" onClear={() => reset()} onClose={onClose}>
          {!isAdmin && (
            <div className="new-referral-top-bar-actions">
              <DefaultButton
                name="Create Referral Request"
                className="blue"
                style={{ width: '210px', marginLeft: '15px' }}
                icon="file-plus"
                onClick={() => setRequestModal(true)}
              />
              <DefaultButton name="Pin to Side" icon="thumbtack" onClick={onPinToSide} />
            </div>
          )}
        </TopBar>
      )}
      <div className="details-panel">
        {confirmationModal && (
          <ConfirmationDialog
            title="Referral Unavailable"
            message={
              "The contact you're referring to is unavailable. Are you sure you want to proceed?"
            }
            open={confirmationModal}
            //@ts-ignore
            onClose={setConfirmationModal}
            onCancel={() => setConfirmationModal(false)}
            onConfirm={() => {
              acknowledgedAvailability.current = true;
              handleSubmit(previewReferralWrapper)();
              setConfirmationModal(false);
            }}
          />
        )}
        {attachmentModal && (
          <ReferralAttachmentModal
            setAttachmentModal={setAttachmentModal}
            attachmentModal={attachmentModal}
            patient={patient}
            formAttachments={formAttachments}
            change={(field: string, value: any) => setValue(field as keyof ReferralValues, value)}
          />
        )}
        {referralModal && (
          <ReferralModal
            setReferralModal={setReferralModal}
            referralModal={referralModal}
            hideSend={isAdmin}
            hideFromPatient={hideFromPatient}
            physician={physician}
            onSaveDocument={onSaveDocument}
            patient={patient}
          />
        )}
        {requestModal && (
          <RequestModal
            setRequestModal={setRequestModal}
            requestModal={requestModal}
            patient={patient}
          />
        )}
        {previewModal && (
          <PreviewModal
            pdfData={pdfData}
            previewModal={previewModal}
            setPreviewModal={setPreviewModal}
            addReferral={handleSubmit(addReferralWrapper)}
            addReferralWithFax={handleSubmit(addReferralWithFax)}
            addReferralWithEmail={handleSubmit(addReferralWithEmail)}
            isIntern={isIntern}
            send_to={send_to}
          />
        )}
        <div className="form">
          <ContactRow patient={patient} contact={send_to} onSelect={onSelectContact} />
          <div className="toggles">
            <DefaultSwitch
              onChange={(_event: unknown, isInputChecked: boolean) =>
                setIncludeCover(isInputChecked)
              }
              label="Include Cover Letter"
              padded={false}
              checked={includeCover}
            />
            <DefaultSwitch
              onChange={(_event: unknown, isInputChecked: boolean) => toggleCpp(isInputChecked)}
              label="Include CPP"
              padded={false}
              checked={includeCpp}
              disabled={!loadedHr}
            />
            <DefaultSwitch name="urgent" control={control} label="Urgent" />
            <DefaultSwitch name="hide" control={control} label="Hide from Patient" />
            <IconButton onClick={importDefaultLetter} className="import">
              <Icon src="file-lines" alt="Import Default Letter + Records" showTooltip={true} />
            </IconButton>
          </div>
          {copyFrom?.instructions && (
            <div className="">
              <span className="small-title">Practitioner Instructions</span>
              <span>{copyFrom?.instructions}</span>
            </div>
          )}
          {includeCover ? (
            loadedHr ? (
              <div className="note-wrapper">
                <TextareaAutosize
                  name="text"
                  control={control}
                  minRows={15}
                  disabled={loading}
                  cacheMeasurements
                />
              </div>
            ) : (
              <ProgressMessage size={35} />
            )
          ) : (
            <br />
          )}
          {includeCpp && loadedHr && (
            <div className="note-wrapper">
              <span className="small-title">CPP</span>
              <TextareaAutosize
                name="cpp"
                control={control}
                minRows={12}
                disabled={loading}
                cacheMeasurements
              />
            </div>
          )}
          <div className="bottom-row">
            <DefaultButton
              name={`${formAttachments.length} attachment${
                formAttachments.length === 1 ? '' : 's'
              }`}
              icon="paperclip"
              onClick={() => setAttachmentModal(true)}
              disabled={loading}
            />
            <DefaultButton
              name="Attach New Referral Template"
              onClick={() => setReferralModal(true)}
              disabled={loading}
              style={{ width: '230px' }}
            />
            <div className="save-wrapper">
              <DefaultButton
                name="Preview Referral"
                disabled={previewDisabled}
                onClick={onClickPreview}
                loading={loading}
                className="blue"
                icon="file-pdf"
                style={{ width: '180px' }}
              />
            </div>
          </div>
          {formAttachments.length > 1 && (
            <>
              <span className="small-title">Reorder Attachments</span>
              <StackItemsView
                stack={wrappedAttachments}
                hideStatus
                describeItem={(item: { id: string; filename: string }) => {
                  const doc = patientDocuments?.find((d: any) => d.filename === item.filename);
                  if (doc?.title) return doc.title;
                  const rx = patientPrescriptions?.find(
                    (p: any) => p.attachments?.[0] === item.filename,
                  );
                  if (rx?.medications?.[0]?.name) return rx.medications[0].name;
                  return item.filename;
                }}
                onMoveEnd={(newList: { id: string; filename: string }[]) => {
                  setValue(
                    'attachments',
                    newList.map((item) => item.filename),
                  );
                }}
                onDeleteItem={(item: { id: string; filename: string }) => {
                  setValue(
                    'attachments',
                    formAttachments.filter((f: string) => f !== item.filename),
                  );
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const ContactRow = ({
  patient,
  contact,
  onSelect,
}: {
  patient: any;
  contact: any;
  onSelect: (contact: any) => void;
}) => {
  const [contactsModal, setContactsModal] = useState(false);

  function onSelectWrapper(contact: any) {
    setContactsModal(false);
    onSelect(contact);
  }

  return (
    <div className="contact-row">
      {contactsModal && (
        <DefaultModal
          key="consultant-search-modal"
          className="fit-modal"
          content={
            //@ts-ignore
            <ContactsSearchModal patient={patient} onSelect={onSelectWrapper} title="Contacts" />
          }
          isOpen={contactsModal}
          onRequestClose={() => setContactsModal(false)}
        />
      )}
      {!contact && (
        <ContactsSearch
          patient={patient}
          onSelect={onSelectWrapper}
          addScreen={false}
          contact={undefined}
          //@ts-ignore
          inlineSearch={true}
        />
      )}
      {!!contact && (
        <div className="contact-badge-wrapper">
          {/*@ts-ignore*/}
          <ContactBadge contact={contact} />
          <Icon className="remove-icon" src="xmark" onClick={() => onSelectWrapper(null)} />
        </div>
      )}
      <div>
        <DefaultButton
          name="Add Contacts"
          icon="user-doctor"
          style={{
            width: '140px',
            position: 'relative',
          }}
          onClick={() => setContactsModal(true)}
        />
      </div>
      <div className="cpso-favorites">
        <a
          href="https://doctors.cpso.on.ca/?search=general"
          target="_blank"
          rel="noopener noreferrer"
        >
          CPSO Search
        </a>
      </div>
    </div>
  );
};

export const ReferralAttachmentModal = ({
  setAttachmentModal,
  attachmentModal,
  useIds,
  patient,
  formAttachments,
  change,
}: {
  setAttachmentModal: (open: boolean) => void;
  attachmentModal: boolean;
  useIds?: boolean;
  patient: any;
  formAttachments: any[];
  change: (field: string, value: any) => void;
}) =>
  attachmentModal ? (
    <DefaultModal
      key="add-document-attachment-modal"
      className="large-modal add-document-attachment-modal"
      wrap={true}
      padding={false}
      title="Add Referral Attachments"
      content={
        <AddDocumentAttachmentModal
          patient={patient}
          selected={formAttachments || []}
          onSubmit={(attachments: any) => {
            change('attachments', attachments);
            setAttachmentModal(false);
          }}
          //@ts-ignore
          useIds={useIds}
        />
      }
      isOpen={attachmentModal}
      onRequestClose={() => setAttachmentModal(false)}
    />
  ) : null;

const RequestModal = ({
  requestModal,
  setRequestModal,
  patient,
}: {
  requestModal: boolean;
  setRequestModal: (open: boolean) => void;
  patient: any;
}) => {
  const dispatch = useAppDispatch();
  return requestModal ? (
    <DefaultModal
      className="fit-modal referral-request-modal"
      key="create-referral-request-modal"
      title="Create Referral Request"
      wrap={true}
      padding={true}
      content={<NewReferralRequest patient={patient} onSuccess={() => setRequestModal(false)} />}
      isOpen={requestModal}
      onRequestClose={() => setRequestModal(false)}
      onPin={() => {
        dispatch(
          uiActions.addPinnedDocument({
            id: `referral-request-${patient.id}`,
            type: 'referral_request',
            patientId: patient.id,
          }),
        );
        setRequestModal(false);
      }}
      pinStyle={{ right: '40px' }}
    />
  ) : null;
};

const ReferralModal = ({
  setReferralModal,
  referralModal,
  hideSend,
  hideFromPatient,
  physician,
  onSaveDocument,
  patient,
}: {
  setReferralModal: (open: boolean) => void;
  referralModal: boolean;
  hideSend: boolean;
  hideFromPatient?: boolean;
  physician: any;
  onSaveDocument: (document: any) => void;
  patient: any;
}) =>
  referralModal ? (
    <DefaultModal
      key="add-referral-modal"
      title="Referral Templates"
      wrap={true}
      padding={true}
      content={
        <DocumentTemplateList
          patient={patient}
          //@ts-ignore
          showCategories={[DocumentUtils.DOCUMENT_TYPE.referral + 's']}
          showSecondaryName={true}
          physician={physician}
          hideTitle={true}
          hideSend={hideSend}
          hidePatientFill={hideSend}
          defaultSavedDocumentCategory={DocumentUtils.DOCUMENT_TYPE.referral}
          onSave={(document: any) => onSaveDocument(document)}
          hideFromPatient={hideFromPatient}
        />
      }
      isOpen={referralModal}
      onRequestClose={() => setReferralModal(false)}
    />
  ) : null;

const PreviewModal = ({
  pdfData,
  previewModal,
  setPreviewModal,
  addReferral,
  addReferralWithFax,
  addReferralWithEmail,
  isIntern,
  send_to,
}: {
  pdfData: any;
  previewModal: boolean;
  setPreviewModal: (open: boolean) => void;
  addReferral: () => void;
  addReferralWithFax: () => void;
  addReferralWithEmail: () => void;
  isIntern: any;
  send_to: any;
}) =>
  previewModal ? (
    <DefaultModal
      className="large-modal"
      key="preview-document-modal"
      content={
        //@ts-ignore
        <PreviewDocumentModal
          url={pdfData?.url}
          type={pdfData?.type}
          onClose={() => setPreviewModal(false)}
          button={
            <>
              <DefaultButton
                name="Save"
                onClick={addReferral}
                icon="floppy-disk"
                style={{
                  width: '190px',
                  marginTop: '5px',
                  display: 'inline-block',
                }}
              />
              {!isIntern && (
                <DefaultButton
                  name="Save and Fax"
                  icon="fax"
                  onClick={addReferralWithFax}
                  disabled={!send_to?.fax}
                  style={{
                    width: '190px',
                    marginTop: '5px',
                    marginLeft: '10px',
                    display: 'inline-block',
                  }}
                />
              )}
              {!isIntern && (
                <DefaultButton
                  name="Save and Email"
                  icon="envelope"
                  onClick={addReferralWithEmail}
                  disabled={!send_to?.email}
                  style={{
                    width: '190px',
                    marginTop: '5px',
                    marginLeft: '10px',
                    display: 'inline-block',
                  }}
                />
              )}
            </>
          }
        />
      }
      isOpen={previewModal}
      onRequestClose={() => setPreviewModal(false)}
    />
  ) : null;

export default NewReferral;
